import { Book, BookOpen, FileAudio, Heart, ListVideo, ThumbsUp, TvMinimalPlay, Video, User } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import styles from './Dashboard.module.css';
import { useSeries } from '../../hooks/admin/useAdminSeries';
import { useAudios } from '../../hooks/admin/useAdminAudios'; // Import the useAudios hook
import { useVideos } from '../../hooks/admin/useAdminVideoUpload'; // Import the useVideos hook for video count
import { useBlogs } from '../../hooks/admin/useBlogs'; // Import the useBlogs hook for blogs
import { useEbooks } from '../../hooks/admin/useAdminEbooks'; // Import the useEbooks hook for eBook count
import { useShorts } from '../../hooks/admin/useAdminShorts'; // Import the useShorts hook for shorts data

function DashboardContent() {
  const { series, isLoading: isLoadingSeries } = useSeries();
  const { audios, isLoading: isLoadingAudios } = useAudios(); // Fetch audio data
  const { videos, isLoading: isLoadingVideos } = useVideos(); // Fetch video data
  const { blogs, isLoading: isLoadingBlogs } = useBlogs(); // Fetch blog data
  const { ebooks, isLoading: isLoadingEbooks } = useEbooks(); // Fetch eBook data
  const { shortsData, isLoading: isLoadingShorts } = useShorts(); // Fetch shorts data

  const [statsData, setStatsData] = useState({
    likes: { count: 0, label: 'Total Likes', trend: '+12% this week' },
    views: { count: 0, label: 'Total Views', trend: '+20% this week' },
    totalUsers: { count: 0, label: 'Total Users', trend: '+10% this month' },
    favorites: { count: 0, label: 'Favorites', trend: '+15% this month' },
    seriesCount: 0,
    audioCount: 0, // Add audio count
    videoCount: 0, // Add video count
    ebookCount: 0, // Add eBook count
    shortsCount: 0, // Add shorts count dynamically
  });

  useEffect(() => {
    if (series) {
      setStatsData((prevData) => ({
        ...prevData,
        seriesCount: series.length,
      }));
    }
  }, [series]);

  useEffect(() => {
    if (audios) {
      setStatsData((prevData) => ({
        ...prevData,
        audioCount: audios.length, // Update audio count
      }));
    }
  }, [audios]);

  useEffect(() => {
    if (videos) {
      setStatsData((prevData) => ({
        ...prevData,
        videoCount: videos.length, // Update video count
      }));
    }
  }, [videos]);

  useEffect(() => {
    if (blogs) {
      setStatsData((prevData) => ({
        ...prevData,
        blogsCount: blogs.length, // Update blog count
      }));
    }
  }, [blogs]);

  useEffect(() => {
    if (ebooks) {
      setStatsData((prevData) => ({
        ...prevData,
        ebookCount: ebooks.length, // Update eBook count
      }));
    }
  }, [ebooks]);

  useEffect(() => {
    if (shortsData) {
      setStatsData((prevData) => ({
        ...prevData,
        shortsCount: shortsData.length, // Dynamically update shorts count
      }));
    }
  }, [shortsData]);

  const cards = [
    { key: 'likes', icon: <ThumbsUp size={24} />, data: statsData.likes },
    { key: 'views', icon: <TvMinimalPlay size={24} />, data: statsData.views },
    { key: 'totalUsers', icon: <User size={24} />, data: statsData.totalUsers },
    { key: 'favorites', icon: <Heart size={24} />, data: statsData.favorites },
  ];

  return (
    <>
      <div className={styles.statsGrid}>
        {/* E-Books stat card */}
        <div className={styles.statCard} style={{ backgroundColor: '#3f51b570' }}>
          <Book size={24} />
          <h3>E-Books</h3>
          <p>{isLoadingEbooks ? 'Loading...' : statsData.ebookCount}</p> {/* Dynamically display eBook count */}
        </div>
        <div className={styles.statCard} style={{ backgroundColor: '#b5b0179c' }}>
          <BookOpen size={24} />
          <h3>Blogs</h3>
          <p>{isLoadingBlogs ? 'Loading...' : statsData.blogsCount}</p> {/* Display blog count */}
        </div>
        <div className={styles.statCard} style={{ backgroundColor: '#4caf5070' }}>
          <FileAudio size={24} />
          <h3>Audio</h3>
          <p>{isLoadingAudios ? 'Loading...' : statsData.audioCount}</p> {/* Display audio count */}
        </div>
        <div className={styles.statCard} style={{ backgroundColor: '#00bcd470' }}>
          <Video size={24} />
          <h3>Videos</h3>
          <p>{isLoadingVideos ? 'Loading...' : statsData.videoCount}</p> {/* Display video count */}
        </div>
        {/* Shorts stat card with dynamic count */}
        <div className={styles.statCard} style={{ backgroundColor: '#9c27b070' }}>
          <TvMinimalPlay size={24} />
          <h3>Shorts</h3>
          <p>{isLoadingShorts ? 'Loading...' : statsData.shortsCount}</p> {/* Dynamically display shorts count */}
        </div>
        <div className={styles.statCard} style={{ backgroundColor: 'rgb(125 0 59)' }}>
          <ListVideo size={24} />
          <h3>Series</h3>
          <p>{isLoadingSeries ? 'Loading...' : statsData.seriesCount}</p> {/* Display series count */}
        </div>
      </div>

      <div className={styles.lowerDiv}>
        {cards.map((card) => (
          <div key={card.key} className={styles.insideDiv}>
            <div className={styles.iconWrapper}>{card.icon}</div>
            <h3>{card.data.count}</h3>
            <p className={styles.label}>
              {card.data.label}
              <span className={styles.trend}>{card.data.trend}</span>
            </p>
          </div>
        ))}
      </div>
    </>
  );
}

export default DashboardContent;
